import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line
} from 'recharts';
import axios from 'axios';
import { EmptyState, MetricCard, PageHeader, useFeedback } from '../components/ui';
import EventReportPanel from './EventReportPanel';

import { API_BASE_URL } from '../config/apiBase';
import { formatChartDay, formatReportDateTime } from '../utils/reportFormat';

const tooltipStyle = {
  backgroundColor: '#111827',
  border: '1px solid #1F293D',
  color: '#F8FAFC',
};

const Reports = () => {
  const { notify } = useFeedback();
  const [loading, setLoading] = useState(false);
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState('');
  const [dateRange, setDateRange] = useState({
    start: new Date().toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0]
  });
  const [reportData, setReportData] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [error, setError] = useState('');
  const [exporting, setExporting] = useState('');
  const [category, setCategory] = useState(0);

  useEffect(() => {
    carregarOrganizacoes();
  }, []);

  const carregarOrganizacoes = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/organizations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrganizations(response.data);
    } catch (err) {
      console.error('Erro ao carregar organizações:', err);
    }
  };

  const gerarRelatorio = async () => {
    if (!selectedOrg) {
      setError('Selecione uma organização');
      return;
    }

    if (dateRange.start && dateRange.end && dateRange.start > dateRange.end) {
      setError('A data inicial não pode ser posterior à data final.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/relatorios/gerar`, {
        params: {
          organizacao_id: selectedOrg,
          data_inicio: dateRange.start,
          data_fim: dateRange.end
        },
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setReportData(response.data);
    } catch (err) {
      const message = err.response?.data?.message || 'Não foi possível gerar o relatório.';
      setError(message);
      notify(message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const exportarRelatorio = async (formato) => {
    if (!reportData?.id || exporting) return;
    setExporting(formato);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/relatorios/${reportData.id}/exportar`, {
        params: { formato },
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const type = formato === 'pdf' ? 'application/pdf' : 'text/csv;charset=utf-8';
      const blob = new Blob([response.data], { type });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `relatorio-${reportData.id}.${formato}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      notify(formato === 'pdf' ? 'PDF exportado.' : 'CSV exportado.', 'success');
    } catch (err) {
      let message = 'Não foi possível exportar o relatório.';
      const data = err.response?.data;
      if (data instanceof Blob) {
        try {
          const parsed = JSON.parse(await data.text());
          if (parsed.message) message = parsed.message;
        } catch (parseError) {
          message = 'Não foi possível exportar o relatório.';
        }
      } else if (err.response?.status === 404) {
        message = 'Relatório não encontrado.';
      } else if (err.response?.data?.message) {
        message = err.response.data.message;
      }
      setError(message);
      notify(message, 'error');
    } finally {
      setExporting('');
    }
  };

  const formatarData = (data) => formatReportDateTime(data);

  const coresGrafico = ['#14B8A6', '#10B981', '#F59E0B', '#EF4444', '#94A3B8'];

  const TabPanel = ({ children, value, index }) => (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );

  return (
    <Box sx={{ minWidth: 0, maxWidth: '100%' }}>
      <Box sx={{ flexGrow: 1, minWidth: 0, maxWidth: '100%' }}>
        <PageHeader
          title="Relatórios"
          subtitle="Controle de ponto e presença em eventos, com os filtros e as permissões já existentes."
        />
        <Tabs value={category} onChange={(_event, value) => setCategory(value)} sx={{ mb: 2 }}>
          <Tab label="Controle de ponto" />
          <Tab label="Eventos" />
        </Tabs>
        {category === 1 ? <EventReportPanel /> : (
        <>

        {/* Filtros */}
        <Card sx={{ mb: 3, backgroundColor: '#111827', borderRadius: 2 }}>
          <CardContent>
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel sx={{ color: '#A1A8B3' }}>Organização</InputLabel>
                  <Select
                    value={selectedOrg}
                    onChange={(e) => setSelectedOrg(e.target.value)}
                    label="Organização"
                    sx={{
                      color: '#F5F7FA',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#14B8A6',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#14B8A6',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#14B8A6',
                      },
                    }}
                  >
                    {organizations.map((org) => (
                      <MenuItem key={org.id} value={org.id} sx={{ color: '#F5F7FA' }}>
                        {org.nome}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="Data Início"
                  type="date"
                  value={dateRange.start}
                  onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      color: '#F5F7FA',
                      '& fieldset': {
                        borderColor: '#14B8A6',
                      },
                    },
                    '& .MuiInputLabel-root': {
                      color: '#A1A8B3',
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="Data Fim"
                  type="date"
                  value={dateRange.end}
                  onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      color: '#F5F7FA',
                      '& fieldset': {
                        borderColor: '#14B8A6',
                      },
                    },
                    '& .MuiInputLabel-root': {
                      color: '#A1A8B3',
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <Button
                  variant="contained"
                  onClick={gerarRelatorio}
                  disabled={loading}
                  fullWidth
                  startIcon={loading ? <CircularProgress size={20} /> : null}
                >
                  {loading ? 'Gerando...' : 'Gerar Relatório'}
                </Button>
              </Grid>
            </Grid>
            {reportData ? (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2, alignItems: 'center' }}>
                <Button
                  variant="outlined"
                  disabled={!reportData.id || Boolean(exporting)}
                  onClick={() => exportarRelatorio('csv')}
                >
                  {exporting === 'csv' ? 'Exportando CSV...' : 'Exportar CSV'}
                </Button>
                <Button
                  variant="outlined"
                  disabled={!reportData.id || Boolean(exporting)}
                  onClick={() => exportarRelatorio('pdf')}
                >
                  {exporting === 'pdf' ? 'Exportando PDF...' : 'Exportar PDF'}
                </Button>
                {!reportData.id ? (
                  <Typography variant="body2" color="text.secondary">
                    O resultado apareceu na tela, mas não há um relatório salvo para exportar.
                  </Typography>
                ) : null}
              </Box>
            ) : null}
          </CardContent>
        </Card>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!reportData && !loading ? (
          <EmptyState title="Nenhum relatório nesta tela ainda." description="Escolha organização e período e gere a consulta. CSV e PDF ficam disponíveis depois que o relatório é salvo." />
        ) : null}

        {reportData && (
          <Card sx={{ backgroundColor: '#111827', borderRadius: 2 }}>
            <CardContent>
              <Tabs
                value={activeTab}
                onChange={(e, newValue) => setActiveTab(newValue)}
                sx={{
                  '& .MuiTab-root': {
                    color: '#A1A8B3',
                    '&.Mui-selected': {
                      color: '#14B8A6',
                    },
                  },
                  '& .MuiTabs-indicator': {
                    backgroundColor: '#14B8A6',
                  },
                }}
              >
                <Tab label="Resumo" />
                <Tab label="Gráficos" />
                <Tab label="Detalhado" />
                <Tab label="Comparativo" />
              </Tabs>

              {/* Resumo */}
              <TabPanel value={activeTab} index={0}>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, minmax(0, 1fr))' }, gap: 2 }}>
                  <MetricCard label="Usuários" value={reportData.total_usuarios ?? '—'} />
                  <MetricCard label="Pontos" value={reportData.total_pontos ?? '—'} />
                  <MetricCard label="Aprovados" value={reportData.pontos_aprovados ?? '—'} />
                  <MetricCard label="Pendentes" value={reportData.pontos_pendentes ?? '—'} />
                </Box>

                <Box sx={{ mt: 3 }}>
                  <Typography variant="h6" gutterBottom sx={{ color: '#F5F7FA' }}>
                    Resumo por Usuário
                  </Typography>
                  <TableContainer component={Paper} sx={{ backgroundColor: '#111827' }}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ color: '#F5F7FA' }}>Usuário</TableCell>
                          <TableCell sx={{ color: '#F5F7FA' }}>Total Pontos</TableCell>
                          <TableCell sx={{ color: '#F5F7FA' }}>Aprovados</TableCell>
                          <TableCell sx={{ color: '#F5F7FA' }}>Pendentes</TableCell>
                          <TableCell sx={{ color: '#F5F7FA' }}>Rejeitados</TableCell>
                          <TableCell sx={{ color: '#F5F7FA' }}>Taxa Aprovação</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {reportData.usuarios?.map((usuario) => (
                          <TableRow key={usuario.id}>
                            <TableCell sx={{ color: '#F5F7FA' }}>{usuario.nome}</TableCell>
                            <TableCell sx={{ color: '#F5F7FA' }}>{usuario.total_pontos}</TableCell>
                            <TableCell>
                              <Chip label={usuario.aprovados} color="success" size="small" />
                            </TableCell>
                            <TableCell>
                              <Chip label={usuario.pendentes} color="warning" size="small" />
                            </TableCell>
                            <TableCell>
                              <Chip label={usuario.rejeitados} color="error" size="small" />
                            </TableCell>
                            <TableCell sx={{ color: '#F5F7FA' }}>
                              {Number(usuario.total_pontos) > 0
                                ? `${((usuario.aprovados / usuario.total_pontos) * 100).toFixed(1)}%`
                                : '—'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              </TabPanel>

              {/* Gráficos */}
              <TabPanel value={activeTab} index={1}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          Situação dos registros de ponto
                        </Typography>
                        {Number(reportData.pontos_aprovados || 0) + Number(reportData.pontos_pendentes || 0) + Number(reportData.pontos_rejeitados || 0) === 0 ? (
                          <Typography color="text.secondary">Não há registros de ponto neste período.</Typography>
                        ) : (
                        <ResponsiveContainer width="100%" height={300}>
                          <PieChart>
                            <Pie
                              data={[
                                { name: 'Aprovados', value: Number(reportData.pontos_aprovados || 0) },
                                { name: 'Pendentes', value: Number(reportData.pontos_pendentes || 0) },
                                { name: 'Rejeitados', value: Number(reportData.pontos_rejeitados || 0) }
                              ].filter((item) => item.value > 0)}
                              cx="50%"
                              cy="50%"
                              outerRadius={80}
                              dataKey="value"
                            >
                              {[0, 1, 2].map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={coresGrafico[index % coresGrafico.length]} />
                              ))}
                            </Pie>
                            <Tooltip contentStyle={tooltipStyle} />
                            <Legend />
                          </PieChart>
                        </ResponsiveContainer>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          Entradas e saídas por dia
                        </Typography>
                        {(reportData.pontos_por_dia || []).length === 0 ? (
                          <Typography color="text.secondary">Não há registros de ponto neste período.</Typography>
                        ) : (
                        <ResponsiveContainer width="100%" height={300}>
                          <BarChart data={(reportData.pontos_por_dia || []).map((item) => ({ ...item, dia: formatChartDay(item.data) }))} margin={{ bottom: 8, left: 0, right: 8 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#1F293D" />
                            <XAxis dataKey="dia" stroke="#94A3B8" />
                            <YAxis allowDecimals={false} stroke="#94A3B8" />
                            <Tooltip contentStyle={tooltipStyle} />
                            <Legend />
                            <Bar dataKey="entradas" fill="#14B8A6" name="Entradas" />
                            <Bar dataKey="saidas" fill="#10B981" name="Saídas" />
                          </BarChart>
                        </ResponsiveContainer>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </TabPanel>

              {/* Detalhado */}
              <TabPanel value={activeTab} index={2}>
                <Typography variant="h6" gutterBottom>
                  Detalhamento de Pontos
                </Typography>
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Usuário</TableCell>
                        <TableCell>Tipo</TableCell>
                        <TableCell>Data/Hora</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Localização</TableCell>
                        <TableCell>Observações</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {reportData.pontos_detalhados?.map((ponto) => (
                        <TableRow key={ponto.id}>
                          <TableCell>{ponto.usuario_nome}</TableCell>
                          <TableCell>
                            <Chip 
                              label={ponto.tipo} 
                              color={ponto.tipo === 'entrada' ? 'primary' : 'secondary'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>{formatarData(ponto.data_hora)}</TableCell>
                          <TableCell>
                            <Chip
                              label={ponto.status}
                              color={
                                ponto.status === 'aprovado' ? 'success' :
                                ponto.status === 'rejeitado' ? 'error' : 'warning'
                              }
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            {ponto.latitude != null ? Number(ponto.latitude).toFixed(4) : '-'}, {ponto.longitude != null ? Number(ponto.longitude).toFixed(4) : '-'}
                          </TableCell>
                          <TableCell>{ponto.observacoes || '-'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </TabPanel>

              {/* Comparativo */}
              <TabPanel value={activeTab} index={3}>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          Situação dos registros ao longo dos dias
                        </Typography>
                        {(reportData.comparativo_performance || []).length < 2 ? (
                          <Typography color="text.secondary">Não há períodos suficientes para comparação.</Typography>
                        ) : (
                        <ResponsiveContainer width="100%" height={320}>
                          <LineChart data={(reportData.comparativo_performance || []).map((item) => ({ ...item, dia: formatChartDay(item.periodo) }))} margin={{ bottom: 8, right: 8 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#1F293D" />
                            <XAxis dataKey="dia" stroke="#94A3B8" />
                            <YAxis allowDecimals={false} stroke="#94A3B8" />
                            <Tooltip contentStyle={tooltipStyle} />
                            <Legend />
                            <Line type="monotone" dataKey="aprovados" stroke="#14B8A6" name="Aprovados" />
                            <Line type="monotone" dataKey="rejeitados" stroke="#EF4444" name="Rejeitados" />
                            <Line type="monotone" dataKey="pendentes" stroke="#F59E0B" name="Pendentes" />
                          </LineChart>
                        </ResponsiveContainer>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </TabPanel>
            </CardContent>
          </Card>
        )}
        </>
        )}
      </Box>
    </Box>
  );
};

export default Reports;
